import * as Yup from 'yup'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { ChangeEvent, useState } from 'react'
import { PatientMappingForm } from './PatientMappingForm'
import { getFilterLocations, updateMessage } from '../utils'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { selectPatientInfo, setAddDeviceCount } from '../ProrithmSlice'
import { editProrithmMapping } from '../../../services/ProrithmServices'
import { LocationData, selectLocations } from '../../location/locationSlice'
import {
  regex,
  messages,
  constants,
  locationTypes,
  MapToLocationValues,
  ProrithmSocketMessage,
} from '../Constants'
import {
  Box,
  Button,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  FormHelperText,
} from '@mui/material'

interface MapToLocationFormProps {
  mapTo: string
  mappingId: string | null
  categoryType: string | null
  selectedDeviceId: string
  handleClose: () => void
  sendMessageOnSocket: (payload: ProrithmSocketMessage) => void
}

const MapToLocationForm = ({
  mapTo,
  mappingId,
  categoryType,
  selectedDeviceId,
  handleClose,
  sendMessageOnSocket,
}: MapToLocationFormProps) => {
  const dispatch = useAppDispatch()
  const locations = useAppSelector(selectLocations)
  const activeLocations = locations
    ?.filter(
      (l: LocationData) => l?.resource?.status === constants?.activeStatus
    )
    ?.filter(
      (item) => !getFilterLocations()?.includes(item?.resource?.name ?? '')
    )

  const patientInfo = useAppSelector(selectPatientInfo)
  const unidentifiedPatientDetails = useAppSelector(
    (state: any) => state.prorithmDevices.unidentifiedPatientDetails
  )
  const [mapToPatient, setMapToPatient] = useState<string>('')

  const handleMapToPatientChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMapToPatient((event.target as HTMLInputElement).value)
  }

  const formik = useFormik<MapToLocationValues>({
    initialValues: {
      location: '',
      locationType: '',
      locationPhone: '',
    },
    validationSchema: Yup.object({
      location: Yup.string(),
      locationType: Yup.string(),
      locationPhone: Yup.string().matches(
        regex?.validPhone,
        messages?.phoneInvalid
      ),
    }),
    onSubmit: () => {
      handleDeviceMapping()
    },
  })

  const getPatientId = () => {
    switch (mapToPatient) {
      case constants?.unidentified:
        return unidentifiedPatientDetails?.patientId
      case constants?.existing:
        return patientInfo?.muhId
      default:
        return null
    }
  }

  const handleDeviceMapping = async () => {
    const payload = {
      categoryType: mapTo,
      patientId: getPatientId(),
      deviceId: selectedDeviceId,
      location: {
        locationName: formik?.values?.location,
        locationType: formik?.values?.locationType,
        locationPhoneNumber: formik?.values?.locationPhone,
      },
    }
    const response = await editProrithmMapping(mappingId, payload)
    if (response?.success) {
      dispatch(setAddDeviceCount())
      toast(response?.message || messages?.deviceMappedSuccessfully, {
        position: 'bottom-center',
        hideProgressBar: true,
        closeButton: false,
        theme: 'dark',
        autoClose: 4000,
      })

      const updateMappingMessage = {
        messageType: constants?.update,
        messageContent: {
          message: updateMessage(categoryType, selectedDeviceId),
          macId: selectedDeviceId,
          category: mapTo,
        },
      }
      sendMessageOnSocket(updateMappingMessage)
      handleClose()
    }
  }

  return (
    <form onSubmit={formik?.handleSubmit}>
      <Box marginY={1} display='flex' flexWrap='wrap' flexDirection='row'>
        <Box minWidth={250} margin={1}>
          <FormControl fullWidth>
            <InputLabel>{constants?.selectLocation}</InputLabel>
            <Select
              id='location'
              name='location'
              data-testid='location-select'
              onBlur={formik?.handleBlur}
              onChange={formik?.handleChange}
              value={formik?.values?.location}
              label={constants?.selectLocation}
              error={
                formik?.touched?.location && Boolean(formik?.errors?.location)
              }
            >
              {activeLocations?.map((d: LocationData) => (
                <MenuItem key={d?.resource?.id} value={d?.resource?.name}>
                  {d?.resource?.name}
                </MenuItem>
              ))}
            </Select>
            {formik?.touched?.location && formik?.errors?.location && (
              <FormHelperText error>{formik?.errors?.location}</FormHelperText>
            )}
          </FormControl>
        </Box>
        <Box minWidth={250} margin={1}>
          <FormControl fullWidth>
            <InputLabel>{constants?.locationType}</InputLabel>
            <Select
              id='locationType'
              name='locationType'
              onBlur={formik?.handleBlur}
              onChange={formik?.handleChange}
              label={constants?.locationType}
              data-testid='location-type-select'
              value={formik?.values?.locationType}
              error={
                formik?.touched?.locationType &&
                Boolean(formik?.errors?.locationType)
              }
            >
              {locationTypes?.map((item) => {
                return (
                  <MenuItem key={item?.value} value={item?.value}>
                    {item?.label}
                  </MenuItem>
                )
              })}
            </Select>
            {formik?.touched?.locationType && formik?.errors?.locationType && (
              <FormHelperText error>
                {formik?.errors?.locationType}
              </FormHelperText>
            )}
          </FormControl>
        </Box>
        <Box minWidth={250} margin={1}>
          <TextField
            fullWidth
            size='medium'
            variant='outlined'
            id='location-phone'
            name='locationPhone'
            label='Location Phone'
            onBlur={formik?.handleBlur}
            onChange={formik?.handleChange}
            placeholder='Enter phone number'
            data-testid='location-phone-input'
            value={formik?.values?.locationPhone}
            error={
              formik?.touched?.locationPhone &&
              Boolean(formik?.errors?.locationPhone)
            }
            helperText={
              formik?.touched?.locationPhone && formik?.errors?.locationPhone
            }
          />
        </Box>
      </Box>
      <PatientMappingForm
        mapTo={mapTo}
        mapToPatient={mapToPatient}
        handleMapToPatientChange={handleMapToPatientChange}
      />
      <Box marginY={2} display='flex' justifySelf='flex-end' maxWidth={200}>
        <Button onClick={() => handleClose()}>{constants?.close}</Button>
        <Button type='submit' disabled={!formik?.isValid}>
          {constants?.submit}
        </Button>
      </Box>
    </form>
  )
}

export default MapToLocationForm
